import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle, ArrowRight } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_brand-first-delivery/artifacts/y09w914c_Drop24%20Logo.jpg';

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const services = [
  { name: 'Last-Mile Delivery', path: '/delivery' },
  { name: 'Track Your Parcel', path: '/#tracking' },
];

// GTM tracking helper
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }
};

export function Footer() {
  const handleEmailClick = () => {
    trackEvent('contact_click', { contact_type: 'email', location: 'footer', contact_value: 'online@drop24.co.za' });
  };

  const handlePhoneClick = () => {
    trackEvent('contact_click', { contact_type: 'phone', location: 'footer', contact_value: '+27660719068' });
  };

  const handleWhatsAppClick = () => {
    trackEvent('contact_click', { contact_type: 'whatsapp', location: 'footer', contact_value: '+27660719068' });
  };

  return (
    <footer data-testid="main-footer" className="bg-gray-900 text-white">
      {/* Newsletter/CTA Strip */}
      <div className="bg-drop24-red py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-2xl md:text-3xl">Ready to improve your logistics?</h3>
              <p className="text-red-100 mt-1">Let's discuss how Drop24 can help your brand deliver better.</p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-drop24-red hover:bg-gray-100 px-6 py-3 rounded-full font-semibold transition-all"
            >
              Get a Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img
                src={LOGO}
                alt="Drop24"
                className="h-10 w-auto mb-6"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Customer experience delivery solutions for South African online stores. We help online stores extend their customer experience beyond checkout through professional last-mile execution, live delivery visibility and responsive customer support.
            </p>
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
              Ethical Logistics
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-drop24-red flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white text-sm block">Gauteng, South Africa</span>
                  <span className="text-gray-500 text-xs">Mon-Fri 8am-6pm</span>
                </div>
              </li>
              <li>
                <a
                  href="tel:+27660719068"
                  onClick={handlePhoneClick}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5 text-drop24-red flex-shrink-0" />
                  <span className="text-sm">066 071 9068</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:deliveries@drop24.co.za"
                  onClick={() => trackEvent('contact_click', { contact_type: 'email', contact_value: 'deliveries@drop24.co.za' })}
                  data-testid="footer-deliveries-email"
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5 text-drop24-red flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-sm block">deliveries@drop24.co.za</span>
                    <span className="text-gray-500 text-xs">Track Your Delivery</span>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/27660719068"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'footer', contact_value: '+27660719068' })}
                  data-testid="footer-whatsapp-1"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <MessageCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">066 071 9068</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Last Mile Delivery Partners */}
        <div className="mt-12">
          <div className="bg-white rounded-2xl py-10 px-6 md:px-12">
            <div className="text-center mb-10">
              <h4 className="font-semibold text-gray-900 text-xl">Our Last Mile Delivery Partners</h4>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              {/* Gig Fleet Section */}
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                <p className="text-[#5B4BDA] text-sm leading-relaxed order-2 md:order-1 text-center md:text-right max-w-xs">
                  Gig Fleet is a micro-franchising platform that equips delivery riders with bikes, tools, and support to build sustainable income in the gig economy. We empower "Gigpreneurs" with asset ownership, operational backing, and access to consistent delivery opportunities through Drop24.
                </p>
                <a
                  href="https://www.gigfleet.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-gigfleet"
                  className="flex-shrink-0 order-1 md:order-2 transition-opacity hover:opacity-70"
                  onClick={() => trackEvent('partner_click', { partner_name: 'Gig Fleet', partner_url: 'www.gigfleet.co.za' })}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_18b8bc6b-a5e3-4abe-a393-25b68282275d/artifacts/lmcycrw1_Gig%20Fleet%20Logo.png"
                    alt="Gig Fleet Management"
                    className="h-24 w-auto object-contain"
                  />
                </a>
              </div>
              
              {/* MyLifeMatters Section */}
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                <a
                  href="https://www.mylifematters.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-mylifematters"
                  className="flex-shrink-0 transition-opacity hover:opacity-70"
                  onClick={() => trackEvent('partner_click', { partner_name: 'MyLifeMatters', partner_url: 'www.mylifematters.co.za' })}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_18b8bc6b-a5e3-4abe-a393-25b68282275d/artifacts/bbyk75ml_Striking%20Typography%20for%20%23MyLifeMatters.png"
                    alt="#MyLifeMatters"
                    className="h-24 w-auto object-contain"
                  />
                </a>
                <p className="text-[#5B4BDA] text-sm leading-relaxed text-center md:text-left max-w-xs">
                  #MyLifeMatters is a social impact movement focused on the safety, dignity, and wellbeing of delivery riders in South Africa's gig economy. We partner with corporates and communities to create a safer, more ethical logistics ecosystem for those powering last-mile delivery.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Drop24. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              Privacy Policy
            </Link>
            <a
              href="https://www.drop24.co.za"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-website-link"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              www.drop24.co.za
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
